/**
 * Product Page — La Tienda del Padel
 * Handles gallery, variant selection, quantity, tabs, sticky bar, size guide, and related products.
 */
class ProductPage {
  constructor() {
    this.productJson = JSON.parse(
      document.querySelector('[data-product-json]')?.textContent || '{}'
    );
    if (!this.productJson.id) return;
    this.init();
  }

  init() {
    this.cacheElements();
    this.initGallery();
    this.initVariantSelection();
    this.initQuantity();
    this.initBuyNow();
    this.initTabs();
    this.initStickyBar();
    this.initSizeGuide();
    this.loadRelatedProducts();
  }

  cacheElements() {
    this.form = document.querySelector('[data-product-form]');
    this.variantIdInput = document.querySelector('[data-variant-id]');
    this.priceDisplay = document.querySelector('[data-product-price]');
    this.ctaContainer = document.querySelector('[data-product-cta]');
    this.addToCartBtn = document.querySelector('[data-add-to-cart]');
    this.quantityInput = document.querySelector('[data-quantity-input]');
    this.gallery = document.getElementById('product-gallery');
    this.stickyBar = document.getElementById('product-sticky-bar');
    this.stickyPrice = document.querySelector('[data-sticky-price]');
  }

  /* ===== GALLERY ===== */
  initGallery() {
    if (!this.gallery) return;

    this.slides = this.gallery.querySelectorAll('[data-gallery-slide]');
    this.thumbs = this.gallery.querySelectorAll('[data-gallery-thumb]');
    this.dots = this.gallery.querySelectorAll('[data-gallery-dot]');
    this.currentSlide = 0;

    // Thumbnail clicks
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.galleryThumb, 10);
        this.goToSlide(index);
      });
    });

    // Dot clicks
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.galleryDot, 10);
        this.goToSlide(index);
      });
    });

    // Desktop zoom
    this.initDesktopZoom();

    // Mobile carousel IntersectionObserver for dot sync
    this.initMobileCarouselObserver();
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    // On mobile, scroll into view
    if (window.innerWidth < 990) {
      this.slides[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    } else {
      // Desktop: toggle hidden
      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.hidden = false;
          slide.classList.add('product-gallery__slide--active');
        } else {
          slide.hidden = true;
          slide.classList.remove('product-gallery__slide--active');
        }
      });
    }

    // Update thumbs
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('product-gallery__thumb--active', i === index);
    });

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('product-gallery__dot--active', i === index);
    });

    this.currentSlide = index;
  }

  initDesktopZoom() {
    if (window.innerWidth < 990) return;

    this.slides.forEach((slide) => {
      const img = slide.querySelector('.product-gallery__image');
      if (!img) return;

      img.addEventListener('mousemove', (e) => {
        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
        img.classList.add('is-zoomed');
      });

      img.addEventListener('mouseleave', () => {
        img.classList.remove('is-zoomed');
        img.style.transformOrigin = 'center center';
      });
    });
  }

  initMobileCarouselObserver() {
    if (window.innerWidth >= 990) return;

    const mainContainer = this.gallery.querySelector('.product-gallery__main');
    if (!mainContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const slide = entry.target;
            const index = Array.from(this.slides).indexOf(slide);
            if (index !== -1) {
              this.dots.forEach((dot, i) => {
                dot.classList.toggle('product-gallery__dot--active', i === index);
              });
              this.currentSlide = index;
            }
          }
        });
      },
      {
        root: mainContainer,
        threshold: 0.6,
      }
    );

    this.slides.forEach((slide) => observer.observe(slide));
  }

  /* ===== VARIANT SELECTION ===== */
  initVariantSelection() {
    this.optionInputs = document.querySelectorAll('[data-option-input]');
    if (this.optionInputs.length === 0) return;

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.onVariantChange();
      });
    });

    // Check URL for variant param
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      const variant = this.productJson.variants.find(
        (v) => v.id === parseInt(variantId, 10)
      );
      if (variant) {
        this.selectVariantOptions(variant);
        this.updateForVariant(variant);
      }
    }
  }

  selectVariantOptions(variant) {
    variant.options.forEach((optionValue, index) => {
      const input = document.querySelector(
        `[data-option-input][name="option-${this.productJson.options[index].toLowerCase().replace(/\s+/g, '-')}"][value="${optionValue}"]`
      );
      if (input) {
        input.checked = true;
      }
    });
  }

  onVariantChange() {
    const selectedOptions = [];
    const fieldsets = document.querySelectorAll('[data-option-index]');
    fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('[data-option-input]:checked');
      if (checked) selectedOptions.push(checked.value);
    });

    // Find matching variant
    const variant = this.productJson.variants.find((v) => {
      return v.options.every((opt, i) => opt === selectedOptions[i]);
    });

    if (variant) {
      this.updateForVariant(variant);
    }

    // Update option button styles
    this.optionInputs.forEach((input) => {
      const label = input.nextElementSibling;
      if (label) {
        label.classList.toggle(
          'product-info__option-btn--selected',
          input.checked
        );
      }
    });

    document.querySelectorAll('[data-color-option]').forEach((fieldset) => {
      const checked = fieldset.querySelector('[data-option-input]:checked');
      const nameEl = fieldset.querySelector('[data-selected-color-name]');
      if (checked && nameEl) {
        nameEl.textContent = checked.value;
      }
    });
  }

  updateForVariant(variant) {
    // Update hidden variant ID
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    // Update price
    if (this.priceDisplay) {
      let priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const discountPct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        const badgeHtml = discountPct > 5 ? `<span class="price__badge">-${discountPct}%</span>` : '';
        priceHtml = `
          <div class="price">
            <s class="price__compare">${this.formatMoney(variant.compare_at_price)}</s>
            <span class="price__sale">${this.formatMoney(variant.price)}</span>
            ${badgeHtml}
          </div>
        `;
      } else {
        priceHtml = `
          <div class="price">
            <span class="price__regular">${this.formatMoney(variant.price)}</span>
          </div>
        `;
      }
      this.priceDisplay.innerHTML = priceHtml;

      // Update sticky bar price too
      if (this.stickyPrice) {
        this.stickyPrice.innerHTML = priceHtml;
      }
    }

    // Update availability
    if (this.ctaContainer) {
      if (variant.available) {
        this.ctaContainer.innerHTML = `
          <button type="submit" class="btn btn-primary btn-full product-info__add-to-cart" data-add-to-cart id="main-add-to-cart">
            Add to Cart
          </button>
          <button type="button" class="btn btn-secondary btn-full product-info__buy-now" data-buy-now>
            Buy Now
          </button>
        `;
        // Re-bind buy now
        this.addToCartBtn = document.querySelector('[data-add-to-cart]');
        this.initBuyNow();
      } else {
        this.ctaContainer.innerHTML = `
          <div class="notify-me" id="notify-me">
            <p class="notify-me__message">This variant is currently sold out</p>
            <p class="notify-me__sub">Enter your email and we'll notify you when it's back in stock.</p>
            <div class="notify-me__form">
              <input type="email" placeholder="Your email address" required aria-label="Email address for stock notification" class="notify-me__input">
              <button type="button" class="btn btn-primary notify-me__btn">Notify Me</button>
            </div>
          </div>
        `;
      }

      // Re-point the sticky bar at whatever the CTA area now holds. This has to
      // run for BOTH branches: when it only ran for available variants, picking
      // a sold-out one left the bar up still offering "Add to cart".
      this.initStickyBar();
    }

    // Update gallery to show variant image
    if (variant.featured_media) {
      const mediaId = variant.featured_media.id;
      const slideIndex = Array.from(this.slides).findIndex(
        (s) => parseInt(s.dataset.mediaId, 10) === mediaId
      );
      if (slideIndex !== -1) {
        this.goToSlide(slideIndex);
      }
    }

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }

  formatMoney(cents) {
    // Shopify stores prices in the smallest currency unit
    const amount = (cents / 100).toFixed(2);
    // Remove trailing zeros
    const formatted = parseFloat(amount).toString();
    return `${this.getCurrencySymbol()}${formatted}`;
  }

  getCurrencySymbol() {
    // Try to get from existing price display
    const existing = document.querySelector('.price__regular, .price__sale');
    if (existing) {
      const text = existing.textContent.trim();
      const match = text.match(/^([^\d]+)/);
      if (match) return match[1];
    }
    return 'EGP ';
  }

  /* ===== QUANTITY ===== */
  initQuantity() {
    const minusBtn = document.querySelector('[data-quantity-minus]');
    const plusBtn = document.querySelector('[data-quantity-plus]');

    if (minusBtn) {
      minusBtn.addEventListener('click', () => {
        if (this.quantityInput) {
          const current = parseInt(this.quantityInput.value, 10) || 1;
          if (current > 1) this.quantityInput.value = current - 1;
        }
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', () => {
        if (this.quantityInput) {
          const current = parseInt(this.quantityInput.value, 10) || 1;
          this.quantityInput.value = current + 1;
        }
      });
    }

    // Enforce min
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => {
        const val = parseInt(this.quantityInput.value, 10);
        if (!val || val < 1) this.quantityInput.value = 1;
      });
    }
  }

  /* ===== BUY NOW ===== */
  initBuyNow() {
    const buyNowBtn = document.querySelector('[data-buy-now]');
    if (!buyNowBtn || !this.form) return;

    buyNowBtn.addEventListener('click', () => {
      // Add hidden return_to input
      let returnInput = this.form.querySelector('input[name="return_to"]');
      if (!returnInput) {
        returnInput = document.createElement('input');
        returnInput.type = 'hidden';
        returnInput.name = 'return_to';
        this.form.appendChild(returnInput);
      }
      returnInput.value = '/checkout';
      this.form.submit();
    });
  }

  /* ===== TABS ===== */
  initTabs() {
    const tabs = document.querySelectorAll('[data-tab]');
    const panels = document.querySelectorAll('[data-tab-panel]');
    if (tabs.length === 0) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;

        // Update tabs
        tabs.forEach((t) => {
          const isActive = t.dataset.tab === target;
          t.classList.toggle('product-tabs__tab--active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        // Update panels
        panels.forEach((panel) => {
          const isActive = panel.dataset.tabPanel === target;
          panel.classList.toggle('product-tabs__panel--active', isActive);
          panel.hidden = !isActive;
        });
      });
    });
  }

  /* ===== MOBILE STICKY BAR ===== */
  /* Mirrored on <body> so the other fixed bottom layers — WhatsApp, the Casa
     launcher, the comparison bar — can react in CSS instead of being nudged
     with inline styles, which lose to the nav's !important rules. */
  setStickyBarVisible(visible) {
    if (!this.stickyBar) return;
    this.stickyBar.hidden = !visible;
    document.body.classList.toggle('sticky-bar-visible', visible);
  }

  initStickyBar() {
    if (!this.stickyBar) return;

    // Runs again on every variant change, so always drop the old observer.
    if (this.stickyObserver) {
      this.stickyObserver.disconnect();
      this.stickyObserver = null;
    }

    // A sold-out variant swaps the button out for the Notify Me form. Bail
    // out without this and the bar keeps whatever state it had, offering
    // "Add to cart" for a variant that cannot be bought.
    const addToCartBtn = document.getElementById('main-add-to-cart');
    if (!addToCartBtn) {
      this.setStickyBarVisible(false);
      return;
    }

    this.stickyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.setStickyBarVisible(!entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    this.stickyObserver.observe(addToCartBtn);

    // Sticky bar add to cart triggers main form
    const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (this.form) {
          // Remove any return_to input so it goes to cart, not checkout
          const returnInput = this.form.querySelector('input[name="return_to"]');
          if (returnInput) returnInput.remove();
          this.form.requestSubmit
            ? this.form.requestSubmit()
            : this.form.submit();
        }
      });
    }
  }

  /* ===== SIZE GUIDE MODAL ===== */
  initSizeGuide() {
    const modal = document.getElementById('size-guide-modal');
    if (!modal) return;

    const openBtns = document.querySelectorAll('[data-size-guide-toggle]');
    const closeBtns = modal.querySelectorAll('[data-size-guide-close]');
    let previouslyFocused = null;

    const openModal = () => {
      previouslyFocused = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('no-scroll');

      // Focus first focusable element
      const firstFocusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) firstFocusable.focus();
    };

    const closeModal = () => {
      modal.hidden = true;
      document.body.classList.remove('no-scroll');
      if (previouslyFocused) previouslyFocused.focus();
    };

    openBtns.forEach((btn) => btn.addEventListener('click', openModal));
    closeBtns.forEach((btn) => btn.addEventListener('click', closeModal));

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });

    // Focus trap
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      const focusableEls = modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          lastEl.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastEl) {
          firstEl.focus();
          e.preventDefault();
        }
      }
    });
  }

  /* ===== RELATED PRODUCTS (Smart Cross-sell) ===== */
  async loadRelatedProducts() {
    const container = document.querySelector('[data-related-products]');
    if (!container) return;

    try {
      // First try complementary products (bags, grips, balls for a racket page)
      let products = await this.fetchRecommendations('complementary');

      // If no complementary results, fall back to default recommendations
      if (!products || products.length === 0) {
        products = await this.fetchRecommendations();
      }

      if (!products || products.length === 0) {
        document.getElementById('product-related')?.remove();
        return;
      }

      container.innerHTML = products
        .map((product) => this.renderProductCard(product))
        .join('');
    } catch (err) {
      console.warn('Failed to load related products:', err);
      document.getElementById('product-related')?.remove();
    }
  }

  async fetchRecommendations(intent) {
    let url = `/recommendations/products.json?product_id=${this.productJson.id}&limit=8`;
    if (intent) {
      url += `&intent=${intent}`;
    }
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await response.json();
    return data.products || null;
  }

  renderProductCard(product) {
    const image = product.featured_image
      ? `<img
          class="product-card__image product-card__image--primary"
          src="${product.featured_image}"
          alt="${this.escapeHtml(product.title)}"
          loading="lazy"
          width="400"
          height="400"
        >`
      : '<div class="product-card__image-placeholder"></div>';

    const price =
      product.compare_at_price && product.compare_at_price > product.price
        ? `<div class="price">
            <s class="price__compare">${this.formatMoney(product.compare_at_price)}</s>
            <span class="price__sale">${this.formatMoney(product.price)}</span>
          </div>`
        : `<div class="price">
            <span class="price__regular">${this.formatMoney(product.price)}</span>
          </div>`;

    return `
      <div class="product-card">
        <div class="product-card__image-wrapper">
          <a href="${product.url}" class="product-card__image-link" aria-label="${this.escapeHtml(product.title)}">
            ${image}
          </a>
        </div>
        <div class="product-card__content">
          <h3 class="product-card__title">
            <a href="${product.url}">${this.escapeHtml(product.title)}</a>
          </h3>
          <div class="product-card__price">${price}</div>
          ${product.vendor ? `<p class="product-card__vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
        </div>
      </div>
    `;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => new ProductPage());
